'use client';

import { API_URL, ApiRequestError } from './api/client';
import { getLearnerToken } from './learnerSession';

/**
 * The "write" half of offline support.
 *
 * Mental model:
 *   cache  = temporary local truth (service worker, sw.js)
 *   server = final truth
 *   queue  = writes made offline, waiting to be negotiated
 *   sync   = replaying the queue in order once the network returns
 *
 * Conflict policy: learner-progress writes are monotonic (complete a lesson,
 * save a quiz score, toggle a favorite, rate a course), and an account is
 * effectively single-writer, so in-order last-write-wins replay is correct.
 * If the server rejects an entry (4xx/5xx — the server saw newer truth or
 * invalid data), the entry is dropped: server truth wins over stale local
 * truth. After every successful flush the UI refetches fresh data.
 */

export interface QueuedWrite {
  id: string;
  path: string;
  method: 'POST' | 'PATCH' | 'DELETE';
  body: unknown;
  /** Shown in the sync pill tooltip / debugging. */
  description: string;
  queuedAt: string;
}

export interface SyncState {
  online: boolean;
  pending: number;
  syncing: boolean;
  /** Set when the last flush attempt had server-rejected entries. */
  lastError: string | null;
}

const QUEUE_KEY = 'vn-sync-queue';
/** Entries older than this are abandoned — stale truth nobody wants replayed. */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

let state: SyncState = { online: true, pending: 0, syncing: false, lastError: null };
const listeners = new Set<() => void>();
let initialized = false;

function notify(): void {
  for (const listener of listeners) listener();
}

function setState(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  notify();
}

export function getSyncState(): SyncState {
  return state;
}

export function subscribeSyncState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function loadQueue(): QueuedWrite[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    const queue = raw ? (JSON.parse(raw) as QueuedWrite[]) : [];
    const fresh = queue.filter((entry) => Date.now() - Date.parse(entry.queuedAt) < MAX_AGE_MS);
    if (fresh.length !== queue.length) saveQueue(fresh);
    return fresh;
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedWrite[]): void {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* storage full/blocked — the write is lost, but the UI showed "offline" */
  }
  setState({ pending: queue.length });
}

/** True for fetch() network failures (offline), false for HTTP error replies. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}

/** Store a failed write for later replay. Returns the queued entry. */
export function enqueueWrite(entry: Omit<QueuedWrite, 'id' | 'queuedAt'>): QueuedWrite {
  const queued: QueuedWrite = {
    ...entry,
    id: `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    queuedAt: new Date().toISOString(),
  };
  const queue = loadQueue();
  queue.push(queued);
  saveQueue(queue);
  return queued;
}

/**
 * Replay the queue in order. Stops at the first network failure (still
 * offline); drops entries the server rejects (server truth wins). Returns
 * how many entries were applied.
 */
export async function flushQueue(): Promise<number> {
  if (typeof window === 'undefined' || state.syncing) return 0;
  let queue = loadQueue();
  if (queue.length === 0) {
    setState({ pending: 0 });
    return 0;
  }

  setState({ syncing: true, lastError: null });
  let applied = 0;
  try {
    while (queue.length > 0) {
      const entry = queue[0]!;
      const token = getLearnerToken();
      try {
        const response = await fetch(`${API_URL}${entry.path}`, {
          method: entry.method,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: entry.body !== undefined ? JSON.stringify(entry.body) : undefined,
        });
        if (!response.ok) {
          // Server saw it and said no — drop this entry, keep going.
          setState({ lastError: `"${entry.description}" was rejected (${response.status}).` });
        } else {
          applied += 1;
        }
        queue = queue.slice(1);
        saveQueue(queue);
      } catch {
        // Network is still down — stop and retry on the next 'online' event.
        break;
      }
    }
  } finally {
    setState({ syncing: false, pending: loadQueue().length });
  }
  if (applied > 0) {
    // Local truth was pushed; let interested screens refetch server truth.
    window.dispatchEvent(new CustomEvent('vn-sync-flushed', { detail: { applied } }));
  }
  return applied;
}

/** Wire up online/offline listeners once (called from the root layout). */
export function initOfflineSync(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  setState({ online: navigator.onLine, pending: loadQueue().length });
  window.addEventListener('online', () => {
    setState({ online: true });
    void flushQueue();
  });
  window.addEventListener('offline', () => setState({ online: false }));
  if (navigator.onLine) void flushQueue();
}
